import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps, type ReactNodeViewProps } from '@tiptap/react';
import { useEffect, useState } from 'react';

/**
 * `[[_TOC_]]` and `[[_TOSP_]]` — Azure DevOps wiki macros for a table of
 * contents and a table of sub-pages. They are atoms in the editor: the real
 * rendering happens in ADO, but the TOC preview lists the current headings so
 * authors can see what readers will get.
 */

function macroNode(name: string, marker: string, View: React.ComponentType<ReactNodeViewProps>) {
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`^${escaped}[ \\t]*(?:\\n+|$)`);
  return Node.create({
    name,
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    parseHTML() {
      return [{ tag: `div[data-type="${name}"]` }];
    },
    renderHTML() {
      return ['div', { 'data-type': name, class: 'ado-macro' }, marker];
    },
    renderText() {
      return marker;
    },

    markdownTokenName: name,
    markdownTokenizer: {
      name,
      level: 'block',
      start(src) {
        return src.indexOf(marker);
      },
      tokenize(src) {
        const m = pattern.exec(src);
        if (!m) return undefined;
        return { type: name, raw: m[0] };
      },
    },
    parseMarkdown(_token, h) {
      return h.createNode(name);
    },
    renderMarkdown() {
      return marker;
    },

    addNodeView() {
      return ReactNodeViewRenderer(View);
    },
  });
}

function TocView({ editor, selected }: NodeViewProps) {
  const [headings, setHeadings] = useState<{ level: number; text: string }[]>([]);

  useEffect(() => {
    const collect = () => {
      const found: { level: number; text: string }[] = [];
      editor.state.doc.descendants(n => {
        if (n.type.name === 'heading') found.push({ level: n.attrs.level, text: n.textContent });
      });
      setHeadings(found);
    };
    collect();
    editor.on('update', collect);
    return () => {
      editor.off('update', collect);
    };
  }, [editor]);

  return (
    <NodeViewWrapper className={`ado-macro ado-toc${selected ? ' is-selected' : ''}`} contentEditable={false}>
      <div className="ado-macro-label">Table of contents <code>[[_TOC_]]</code></div>
      {headings.length === 0 ? (
        <div className="ado-macro-hint">Headings on this page will be listed here.</div>
      ) : (
        <ul>
          {headings.map((h, i) => (
            <li key={i} style={{ marginLeft: (h.level - 1) * 14 }}>{h.text || <em>(empty heading)</em>}</li>
          ))}
        </ul>
      )}
    </NodeViewWrapper>
  );
}

function TospView({ selected }: NodeViewProps) {
  return (
    <NodeViewWrapper className={`ado-macro ado-tosp${selected ? ' is-selected' : ''}`} contentEditable={false}>
      <div className="ado-macro-label">Table of sub-pages <code>[[_TOSP_]]</code></div>
      <div className="ado-macro-hint">Azure DevOps lists this page's sub-pages here.</div>
    </NodeViewWrapper>
  );
}

export const AdoToc = macroNode('adoToc', '[[_TOC_]]', TocView);
export const AdoTosp = macroNode('adoTosp', '[[_TOSP_]]', TospView);
